// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { Material, Matrix4, Vector3 } from 'three';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { TextureConnectionInfo } from '@3d-scene/model3d/texture-connection-info';
import { MetaElementVO, TextureAreaUsageVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';

class ConnectionSize {
  public connectionName: string;
  public localRectangle: Rectangle3D;
  public type: string;

  constructor(connection: Connection, type: string) {
    this.connectionName = connection.id;
    this.localRectangle = connection.localRectangle;
    this.type = type;
  }
}

export class ArticleLabelingManager {
  private _defaultConnectionsMap: Dictionary<string, ConnectionSize> = new Dictionary<string, ConnectionSize>();
  private _textConnections: TextureConnectionInfo[];

  constructor(
    private articleContainer: ArticleContainer,
    private labelingUtils: LabelingUtils,
    private containerService: ContainerService
  ) {}

  public get textConnections(): TextureConnectionInfo[] {
    return this._textConnections.filter((connection) => connection.type === TextureAreaVO.TYPE_TEXT || connection.type === TextureAreaVO.TYPE_TEXT_OR_IMAGE_USAGE);
  }

  public get imageConnections(): TextureConnectionInfo[] {
    return this._textConnections.filter((connection) => connection.type === TextureAreaVO.TYPE_IMAGE);
  }

  public refreshTextureConnections(createTextures: boolean = true, updateTextures: boolean = true, deleteUnusedConnections: boolean = true): void {
    const textureAreas: TextureAreaVO[] = ArticleUtils.getTextTextureElements(this.articleContainer.article);
    for (const textureArea of textureAreas) {
      const nameString: string = textureArea.name;
      const connection: Connection = this.articleContainer.findFirstConnectionByName(nameString);
      if (connection) {
        this.updateTextureConnection(connection, createTextures);
      }
    }
    if (textureAreas.length > 0) {
      this.articleContainer.refreshMetaElements(null, deleteUnusedConnections);
      if (updateTextures) {
        this.articleContainer.refreshTextures();
      }
    }
  }

  private updateTextureConnection(connection: Connection, createTextures: boolean): void {
    if (!connection) {
      return;
    }
    const textureUsage: TextureAreaUsageVO = connection.metaElementUsage as TextureAreaUsageVO;
    if (textureUsage) {
      if (!textureUsage.previousMergedUsageId && !textureUsage.nextMergedUsageId) {
        // if connection is not merged with any connection
        this.restoreDefaultConnection(connection, connection.isActive);
      } else if (!textureUsage.previousMergedUsageId) {
        // if connection is start connection in merged list
        this.updateMergedConnection(connection, true);
      } else {
        // if connection is middle or last connection in merged list
        this.restoreDefaultConnection(connection, false);
      }
    }
  }

  private getConnectionResizeDirection(connection: Connection, newRectangle: Rectangle3D): Vector3 {
    const directionVector = connection.localRectangle.center.sub(newRectangle.center);
    return directionVector.normalize();
  }

  public restoreDefaultConnection(connection: Connection, showMesh: boolean): void {
    if (!connection) {
      return;
    }
    const defaultConnectionSize: ConnectionSize = this._defaultConnectionsMap.getValue(connection.id);
    if (!defaultConnectionSize) {
      return;
    }

    const resizeDirection: Vector3 = this.getConnectionResizeDirection(connection, defaultConnectionSize.localRectangle);

    // resize mesh to default width
    const connectionResized: boolean = this.resizeConnection(connection, defaultConnectionSize.localRectangle.width, resizeDirection);
    if (connectionResized) {
      this.moveMesh(connection, defaultConnectionSize.localRectangle);
      this.computeMeshBoundingBox(connection);
    }
    (connection.mesh.material as Material).visible = showMesh;
  }

  private updateMergedConnection(connection: Connection, showMesh: boolean): void {
    const mergedConnections: Connection[] = this.labelingUtils.findNextMergedTextureConnections(connection);
    let commonRect: Rectangle3D = this.calculateBoundingRectangle(mergedConnections);
    const startRect: Rectangle3D = Rectangle3D.fromArray(connection.localRectangle.toArray());

    const differentArticlesConnections: boolean = this.isConnectionsFromDifferentArticles(mergedConnections);
    if (!differentArticlesConnections) {
      commonRect = this.containerService.changeRectangleDirection(commonRect, startRect);
    }

    const resizeDirection: Vector3 = this.getConnectionResizeDirection(connection, commonRect);

    // resize mesh to required width
    const connectionResized: boolean = this.resizeConnection(
      connection,
      commonRect.width,
      resizeDirection,
      !ArticleUtils.isLabelingArticle(connection.articleContainer.article)
    );
    if (connectionResized) {
      this.moveMesh(connection, commonRect);
      this.computeMeshBoundingBox(connection);
    }
    (connection.mesh.material as Material).visible = showMesh;
  }

  private moveMesh(connection: Connection, baseRect: Rectangle3D): void {
    const middlePoint: Vector3 = MathUtils.calculateMiddlePointByRectangle(baseRect);
    const meshCenter: Vector3 = MathUtils.calculateMiddlePointByRectangle(connection.localRectangle);
    if (!MathUtils.equalsNumber3D(middlePoint, meshCenter, 0.001)) {
      const offset: Vector3 = middlePoint.clone().sub(meshCenter);
      FaceUtils.moveMeshByOffset(connection.mesh, offset);
    }
  }

  /**
   * Workaround to make scaled connection selectable in any position
   * (boundingSphere is used by Raycaster to determine intersection with 3d-objects).
   */
  private computeMeshBoundingBox(connection: Connection): void {
    const geometry = connection.mesh.geometry;
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
  }

  private resizeConnection(connection: Connection, targetWidth: number, direction: Vector3 = new Vector3(1, 0, 0), cloneGeometry?: boolean): boolean {
    const currentWidth: number = connection.localRectangle.width;
    if (MathUtils.equalsNumber(currentWidth, targetWidth, 0.001)) {
      return false;
    }

    let geometry = connection.mesh.geometry;
    if (cloneGeometry) {
      geometry = geometry.clone();
      connection.mesh.geometry = geometry;
      connection.mesh.material = (connection.mesh.material as Material).clone();
    }
    geometry.computeBoundingBox();
    const targetPosition: Vector3 = geometry.boundingBox.getCenter(new Vector3());
    const scaleMatrix: Matrix4 = MathUtils.constructBasicScaleMatrix(
      targetPosition,
      targetPosition.clone().add(direction.clone().multiplyScalar(currentWidth)),
      targetPosition.clone().add(direction.clone().multiplyScalar(targetWidth)),
      true,
    );
    FaceUtils.scaleMesh(connection.mesh, geometry.boundingBox.max, scaleMatrix);
    return true;
  }

  private isConnectionsFromDifferentArticles(connections: Connection[]): boolean {
    return ArrayUtils.distinct(connections.map((c) => c.articleContainer.articleGUID)).length > 1;
  }

  private calculateBoundingRectangle(connections: Connection[]): Rectangle3D {
    if (this.isConnectionsFromDifferentArticles(connections)) {
      return this.calculateLocalBoundingRectangleForDifferentContainer(connections);
    }
    return this.calculateLocalBoundingRectangle(connections);
  }

  private calculateLocalBoundingRectangleForDifferentContainer(connections: Connection[]): Rectangle3D {
    const rectangles: Rectangle3D[] = [];
    for (const connection of connections) {
      const defaultConnectionSize: ConnectionSize = this._defaultConnectionsMap.getValue(connection.id);
      if (defaultConnectionSize) {
        const defaultRectangleCopy: Rectangle3D = defaultConnectionSize.localRectangle.clone();
        if (connection.articleContainer) {
          MathUtils.applyObjectTransformationToRectangle(defaultRectangleCopy, connection.articleContainer);
        }
        rectangles.push(defaultRectangleCopy);
      } else {
        rectangles.push(connection.compositionRectangle);
      }
    }
    const commonRect: Rectangle3D = ConnectionInfoUtils.calculateCommonRectangle(rectangles, false);
    connections[0].articleContainer.updateMatrixWorld(true);
    const localRect: Rectangle3D = Rectangle3D.fromArray([
      connections[0].articleContainer.worldToLocal(commonRect.point1),
      connections[0].articleContainer.worldToLocal(commonRect.point2),
      connections[0].articleContainer.worldToLocal(commonRect.point3),
      connections[0].articleContainer.worldToLocal(commonRect.point4),
    ]);
    return localRect;
  }

  private calculateLocalBoundingRectangle(connections: Connection[]): Rectangle3D {
    const rectangles: Rectangle3D[] = [];
    for (const connection of connections) {
      const defaultConnectionSize: ConnectionSize = this._defaultConnectionsMap.getValue(connection.id);
      if (defaultConnectionSize) {
        rectangles.push(defaultConnectionSize.localRectangle.clone());
      } else {
        rectangles.push(connection.localRectangle);
      }
    }

    const commonRect: Rectangle3D = ConnectionInfoUtils.calculateCommonRectangle(rectangles, false);
    return commonRect;
  }

  public fillDefaultTextConnectionsMap(): void {
    if (!this.articleContainer || !this.articleContainer.model || !this.articleContainer.connections) {
      return;
    }
    this._defaultConnectionsMap = new Dictionary<string, ConnectionSize>();
    for (const connection of this.articleContainer.connections) {
      const meta: MetaElementVO = this.articleContainer.findMetaElementByName(connection.id);
      if (meta instanceof TextureAreaVO && this.isPrintableTextureArea(meta)) {
        this._defaultConnectionsMap.setValue(connection.id, new ConnectionSize(connection, meta.textureType));
      }
    }
    this.createTextConnectionsData();
  }

  private isPrintableTextureArea(meta: TextureAreaVO) {
    return meta.textureType === TextureAreaVO.TYPE_TEXT
        || meta.textureType === TextureAreaVO.TYPE_TEXT_OR_IMAGE_USAGE
        || meta.textureType === TextureAreaVO.TYPE_IMAGE;
  }

  private createTextConnectionsData(): void {
    this._textConnections = [];
    let textConnectionSizes: ConnectionSize[] = [];
    for (const defaultSize of this._defaultConnectionsMap.values()) {
      textConnectionSizes.push(defaultSize);
    }

    textConnectionSizes = this.sortLinedConnections(textConnectionSizes);
    let previousTextConnection: TextureConnectionInfo = null;
    let distanceToPreviousConnection: number = 0;
    for (const connectionSize of textConnectionSizes) {
      if (previousTextConnection) {
        const distance: number = connectionSize.localRectangle.point1.distanceTo(previousTextConnection.rectangle.point4);
        distanceToPreviousConnection = distance > 1 /*EPSILON*/ ? distance : 0;
      }
      previousTextConnection = new TextureConnectionInfo(connectionSize.connectionName, connectionSize.localRectangle, distanceToPreviousConnection, connectionSize.type);
      this._textConnections.push(previousTextConnection);
    }
  }

  /**
   * Sorts connections along one of axes (X, Y or Z). The method doesn't modify the original array.
   * Sorting by 'y' and 'z' axis is made in positive direction, sorting by 'x' axis - in negative direction.
   * @param connections the connections to be sorted, they must reside along one line and have the same N-direction
   * @return sorted connections
   */
  private sortLinedConnections(connections: ConnectionSize[]): ConnectionSize[] {
    if (ArrayUtils.isEmpty(connections)) {
      return [];
    }
    if (connections.length === 1) {
      return connections.concat();
    }

    const firstConnection: ConnectionSize = connections[0];
    const secondConnection: ConnectionSize = connections[1];
    const firstConnectionPoint: Vector3 = firstConnection.localRectangle.point1;
    const secondConnectionPoint: Vector3 = secondConnection.localRectangle.point1;
    const axis: string = this.getSortingAxis(firstConnectionPoint, secondConnectionPoint);

    const sortedConnections: ConnectionSize[] = [];
    for (const connection of connections) {
      const coordinate: number = connection.localRectangle.point1[axis];
      if (sortedConnections.length === 0) {
        sortedConnections.push(connection);
      } else {
        let inserted: boolean = false;
        for (let i: number = 0; i < sortedConnections.length; i++) {
          const sortedCoordinate: number = sortedConnections[i].localRectangle.point1[axis];
          const insertConnection: boolean = axis === 'x' ? sortedCoordinate < coordinate : sortedCoordinate > coordinate;
          if (insertConnection) {
            sortedConnections.splice(i, 0, connection);
            inserted = true;
            break;
          }
        }
        if (!inserted) {
          sortedConnections.push(connection);
        }
      }
    }
    return sortedConnections;
  }

  private getSortingAxis(firstConnectionPoint: Vector3, secondConnectionPoint: Vector3): string {
    const deltaX: number = Math.abs(secondConnectionPoint.x - firstConnectionPoint.x);
    const deltaY: number = Math.abs(secondConnectionPoint.y - firstConnectionPoint.y);
    const deltaZ: number = Math.abs(secondConnectionPoint.z - firstConnectionPoint.z);
    let maxDelta: number = -Number.MAX_VALUE;
    const deltas: number[] = [deltaX, deltaY, deltaZ];
    for (const delta of deltas) {
      if (delta > maxDelta) {
        maxDelta = delta;
      }
    }
    switch (maxDelta) {
      case deltaX:
        return 'x';
      case deltaY:
        return 'y';
      case deltaZ:
        return 'z';
    }
  }
}
